import React, { useEffect, useRef, useState } from 'react';
import Button from '../../../components/shared/Button/Button';
import { useCountdown } from '../../../hooks/useCountdown';
import { guestPay, getGroupStatus } from '../../../services/bookings';
import { ManualPaymentModal } from './ManualPaymentModal';

interface CheckoutPanelProps {
  group: { id: number; reference: string; total: string; paybill: string; account_reference: string };
  heldUntil: string | null;
  summary: { departure: string; boardingName: string; alightingName: string; seatLabels: string; fare: string };
  isAuthenticated: boolean;
  bookingPhone: string;
  onPaid: () => void;
  onExpired: () => void;
  onLogin: () => void;
}

export const CheckoutPanel: React.FC<CheckoutPanelProps> = ({
  group, heldUntil, summary, isAuthenticated, bookingPhone, onPaid, onExpired, onLogin,
}) => {
  const { label, expired } = useCountdown(heldUntil);
  const [consent, setConsent] = useState(false);
  const [paying, setPaying] = useState(false);
  const [checking, setChecking] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const onExpiredRef = useRef(onExpired);
  const onPaidRef = useRef(onPaid);
  useEffect(() => { onExpiredRef.current = onExpired; onPaidRef.current = onPaid; });

  useEffect(() => {
    if (expired) onExpiredRef.current();
  }, [expired]);

  useEffect(() => () => {
    mountedRef.current = false;
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const pollStatus = async () => {
    const status = await getGroupStatus(group.reference);
    if (!mountedRef.current) return false;
    if (status.all_paid) {
      if (pollRef.current) clearInterval(pollRef.current);
      onPaidRef.current();
      return true;
    }
    return false;
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => { pollStatus().catch(() => {}); }, 4000);
  };

  const handlePay = async () => {
    setError(null);
    setPaying(true);
    try {
      await guestPay(group.id);
      startPolling();
      // Poll immediately so we don't wait 4 s for the first tick
      pollStatus().catch(() => {});
    } catch {
      setError('Could not start payment. Try again or use manual Paybill.');
    } finally {
      setPaying(false);
    }
  };

  const handleManualCheck = async () => {
    setChecking(true);
    try { await pollStatus(); } finally { setChecking(false); }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* Left: payment details */}
      <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900">Payment Details</h2>
          <span className="rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-white">
            {label} Remaining
          </span>
        </div>

        {!isAuthenticated && (
          <button onClick={onLogin} className="mb-4 text-sm font-medium text-brand-600 hover:underline">
            Log in / create account
          </button>
        )}

        <p className="mb-2 text-xs font-medium text-slate-600">Select Payment Method</p>
        <div className="mb-4 rounded-lg border-2 border-emerald-500 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
          M-Pesa
        </div>

        <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="pay-phone">Phone Number</label>
        <div className="mb-1 flex items-center gap-2">
          <span className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600">+254</span>
          <input
            id="pay-phone" value={bookingPhone} readOnly
            className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700"
          />
        </div>
        <p className="mb-4 text-xs text-slate-500">The M-Pesa prompt is sent to the number you booked with.</p>

        <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Note: Once seats are selected and paid for, tickets are non-refundable.
        </p>

        <label className="mb-4 flex items-start gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
          <span>By proceeding, I consent to collecting and storing my information in accordance with the Privacy Policy and Terms &amp; Conditions.</span>
        </label>

        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

        <Button type="button" fullWidth disabled={!consent || expired} loading={paying} onClick={handlePay}>
          🔒 Pay Securely with Mpesa
        </Button>

        <button onClick={() => setShowManual(true)} className="mt-3 text-sm font-medium text-brand-600 hover:underline">
          Manual Payment Steps
        </button>
      </div>

      {/* Right: trip summary */}
      <aside className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Onward Trip</p>
        <p className="mt-2 text-sm text-slate-700">Departure: {new Date(summary.departure).toLocaleString()}</p>
        <p className="mt-1 text-sm text-slate-700">{summary.boardingName} → {summary.alightingName}</p>
        <p className="mt-1 text-sm text-slate-700">Selected Seats: {summary.seatLabels}</p>
        <hr className="my-3 border-slate-200" />
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fare Breakup</p>
        <div className="mt-2 flex justify-between text-sm text-slate-700"><span>Onward Fare</span><span>KES {summary.fare}</span></div>
        <div className="mt-1 flex justify-between text-sm text-slate-500"><span>Insurance</span><span>KES 0</span></div>
        <div className="mt-1 flex justify-between text-sm text-slate-500"><span>Discount</span><span>KES 0</span></div>
        <hr className="my-3 border-slate-200" />
        <div className="flex justify-between text-base font-bold text-slate-900"><span>Total Payable</span><span>KES {group.total}</span></div>
      </aside>

      {showManual && (
        <ManualPaymentModal
          paybill={group.paybill}
          account={group.account_reference}
          amount={group.total}
          checking={checking}
          onCheck={handleManualCheck}
          onClose={() => setShowManual(false)}
        />
      )}
    </div>
  );
};
